import React from 'react';
import { Link } from 'react-router-dom';
import { Plus, Search } from 'lucide-react';

import { adminContent } from 'services/api';
import type { AdminCatalogPage, QuestionLibrarySearchRow } from 'services/api/adminContentClient';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { cn } from 'lib/utils';

import { formatQuestionTypeSlug, plainQuestionLabel } from '../QuestionLibrary/navigatorUtils';

const RESULT_LIMIT = 8;
const SEARCH_DEBOUNCE_MS = 250;
export const QUESTION_LIBRARY_PATH = '/admin/content?tab=question-library';

export type QuestionAddPickerProps = {
  /** Questions already in the module (marked in the results; adding them again is rejected by the page). */
  existingQuestionIds: ReadonlySet<number>;
  /** Adds a picked search result; resolves true when it was added. */
  onAddResult: (row: QuestionLibrarySearchRow) => boolean | Promise<boolean>;
  /** Enter with nothing highlighted: look the text up as an exact question key or numeric ID. */
  onAddByKey: (keyOrId: string) => Promise<boolean>;
  submitting?: boolean;
  disabled?: boolean;
};

/** Search-to-add for the module's question list (search by text or key; Enter on an exact key or ID still works). */
export const QuestionAddPicker: React.FC<QuestionAddPickerProps> = ({
  existingQuestionIds,
  onAddResult,
  onAddByKey,
  submitting = false,
  disabled = false,
}) => {
  const [query, setQuery] = React.useState('');
  const [debounced, setDebounced] = React.useState('');
  const [results, setResults] = React.useState<QuestionLibrarySearchRow[]>([]);
  const [resultsFor, setResultsFor] = React.useState('');
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState(false);
  const [open, setOpen] = React.useState(false);
  const [activeIndex, setActiveIndex] = React.useState(-1);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const listId = React.useId();

  React.useEffect(() => {
    const handle = window.setTimeout(() => setDebounced(query.trim()), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query]);

  React.useEffect(() => {
    if (!debounced) {
      setResults([]);
      setResultsFor('');
      setLoading(false);
      setError(false);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    setError(false);
    adminContent.questions
      .search({ search: debounced, page_size: RESULT_LIMIT })
      .then((response) => {
        if (cancelled) return;
        const list = (response.data as AdminCatalogPage<QuestionLibrarySearchRow> | undefined)?.results;
        setResults(Array.isArray(list) ? list.slice(0, RESULT_LIMIT) : []);
        setResultsFor(debounced);
        setActiveIndex(-1);
      })
      .catch(() => {
        if (cancelled) return;
        setResults([]);
        setResultsFor(debounced);
        setError(true);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [debounced]);

  React.useEffect(() => {
    if (!open) {
      return undefined;
    }
    const handlePointer = (event: MouseEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handlePointer);
    return () => document.removeEventListener('mousedown', handlePointer);
  }, [open]);

  // Only results for what is typed now can be picked; older ones are still on screen while the next search runs.
  const visibleResults = resultsFor && resultsFor === query.trim() ? results : [];
  const showPopup = open && !!query.trim();

  const reset = () => {
    setQuery('');
    setDebounced('');
    setResults([]);
    setResultsFor('');
    setActiveIndex(-1);
    setOpen(false);
  };

  const addResult = async (row: QuestionLibrarySearchRow) => {
    if (submitting || disabled) return;
    const added = await onAddResult(row);
    if (added) reset();
  };

  const submit = async () => {
    if (submitting || disabled) return;
    const highlighted = activeIndex >= 0 ? visibleResults[activeIndex] : undefined;
    if (highlighted) {
      await addResult(highlighted);
      return;
    }
    const text = query.trim();
    if (!text) return;
    const added = await onAddByKey(text);
    if (added) reset();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      setOpen(true);
      setActiveIndex((current) => Math.min(visibleResults.length - 1, current + 1));
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      setActiveIndex((current) => Math.max(-1, current - 1));
    } else if (event.key === 'Enter') {
      event.preventDefault();
      void submit();
    } else if (event.key === 'Escape') {
      if (showPopup) {
        event.preventDefault();
        event.stopPropagation();
      }
      setOpen(false);
      setActiveIndex(-1);
    }
  };

  const activeId = activeIndex >= 0 && visibleResults[activeIndex] ? `${listId}-${visibleResults[activeIndex].id}` : undefined;

  return (
    <div className="space-y-1.5" ref={rootRef}>
      <form
        className="relative flex flex-col gap-2 sm:flex-row sm:items-center"
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <div className="relative min-w-0 flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden="true" />
          <Input
            value={query}
            onChange={(event) => {
              setQuery(event.target.value);
              setOpen(true);
              setActiveIndex(-1);
            }}
            onFocus={() => setOpen(true)}
            onKeyDown={handleKeyDown}
            placeholder="Add a question: search by text, key, or ID"
            aria-label="Add a question"
            role="combobox"
            aria-autocomplete="list"
            aria-expanded={showPopup}
            aria-controls={listId}
            aria-activedescendant={activeId}
            className="pl-9"
            disabled={disabled}
            data-testid="fb-add-question-input"
          />
          {showPopup ? (
            <div
              className="absolute left-0 right-0 top-full z-30 mt-1 overflow-hidden rounded-2xl bg-white shadow-xl"
              data-testid="fb-add-question-results"
            >
              <ul id={listId} role="listbox" aria-label="Matching questions" className="max-h-80 overflow-y-auto py-1">
                {visibleResults.map((row, index) => {
                  const label = plainQuestionLabel(row.label);
                  const inModule = existingQuestionIds.has(row.id);
                  return (
                    <li
                      key={row.id}
                      id={`${listId}-${row.id}`}
                      role="option"
                      aria-selected={index === activeIndex}
                      data-testid={`fb-add-question-option-${row.id}`}
                      className={cn(
                        'flex cursor-pointer items-center gap-3 px-3 py-2 text-sm',
                        index === activeIndex ? 'bg-foundation-calm-blue-50' : 'hover:bg-foundation-calm-blue-50/60',
                      )}
                      onMouseDown={(event) => event.preventDefault()}
                      onMouseEnter={() => setActiveIndex(index)}
                      onClick={() => void addResult(row)}
                    >
                      <span className="shrink-0 rounded-full bg-foundation-base-darkbase-dark-500/5 px-2 py-0.5 text-[11px] text-muted-foreground">
                        {formatQuestionTypeSlug(row.type)}
                      </span>
                      <span className="min-w-0 flex-1 truncate text-foreground">
                        {label || <span className="italic text-muted-foreground">(empty text)</span>}
                      </span>
                      <span className="max-w-[10rem] shrink-0 truncate font-mono text-[11px] text-muted-foreground">{row.key}</span>
                      {inModule ? (
                        <span className="shrink-0 text-[11px] text-muted-foreground">In this module</span>
                      ) : null}
                    </li>
                  );
                })}
              </ul>
              {loading && visibleResults.length === 0 ? (
                <p className="px-3 py-2 text-xs text-muted-foreground">Searching…</p>
              ) : null}
              {error ? (
                <p className="px-3 py-2 text-xs text-destructive">Search failed. Press Enter to add by exact key or ID.</p>
              ) : null}
              {!loading && !error && resultsFor === query.trim() && visibleResults.length === 0 ? (
                <p className="px-3 py-2 text-xs text-muted-foreground">
                  No questions match. Press Enter to add by exact key or ID.
                </p>
              ) : null}
            </div>
          ) : null}
        </div>
        <Button type="submit" variant="secondary" size="auto" disabled={submitting || disabled || !query.trim()}>
          <Plus className="h-4 w-4" aria-hidden="true" /> {submitting ? 'Adding...' : 'Add question'}
        </Button>
      </form>
      <Link
        to={QUESTION_LIBRARY_PATH}
        className="inline-flex items-center gap-1 text-xs font-medium text-foundation-orange-500 hover:underline"
      >
        <Plus className="h-3 w-3" aria-hidden="true" /> Create a new question in the Question Library
      </Link>
    </div>
  );
};

export default QuestionAddPicker;
